import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { debounce } from 'lodash';

// Actions
import { createComparison, skipEmptyState } from '@/redux/modules/comparison';
import { getContactComparisons, removeContactComparisonsFromCurrent } from '@/redux/modules/contacts';
import { resetGif } from '@/redux/modules/common/actionCreators';
import { setBaseDob } from '@/redux/modules/bmo-gif';

// Components
import ComparisonsTable from '@/views/components/tables/ComparisonsTable';
import EmptyStateCard from '@/views/components/misc/EmptyStateCard';
import { withErrorBoundary } from '@/views/components/errors';
import { ContactDetailsComparisonsHeader } from './components/ContactComparisonsHeader';

// Hooks
import useRedirect from '@/hooks/useRedirect';

// Types
import Comparison from '@/types/comparison';
import { CurrentContact } from '@/redux/modules/types/contacts';
import GenericContact from '@/types/generic-contact';
import Locale from '@/types/locale';
import Nullable from '@/types/nullable';
import ReducedComparison from '@/types/reduced-comparison';
import User from '@/types/user';
import { ReduxState } from '@/redux/modules/types';
import ComparisonContact from '@/types/comparison-contact';

// Utils
import { COMPARISON_NAV_PATHS, NAV_PATHS } from '@/utils/constants';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';

type DispatchProps = {
  createComparison: (contact?: GenericContact) => Promise<Comparison>;
  getContactComparisons: (contact: GenericContact, query?: string) => Promise<Comparison[]>;
  removeContactComparisonsFromCurrent: (comparisons: ReducedComparison[]) => void;
  resetGif: () => void;
  setBaseDob: (contact: ComparisonContact) => void;
  skipEmptyState: () => void;
};

type StateProps = {
  comparisons?: Comparison[];
  currentContact?: CurrentContact;
  isComparisonsLoading: boolean;
  locale: Locale;
  user?: Nullable<User>;
};

type Props = DispatchProps &
  StateProps & {
    onOpenComparison: (comparison: ReducedComparison) => void;
  };

const ContactDetailsComparisons = (props: Props) => {
  const {
    currentContact,
    comparisons,
    isComparisonsLoading,
    createComparison,
    getContactComparisons,
    removeContactComparisonsFromCurrent,
    resetGif,
    setBaseDob,
    skipEmptyState,
    user,
  } = props;

  const i18nEmptyStatePath = 'EmptyStateCard.ContactDetailsComparisons';
  const { redirect } = useRedirect();

  const [comparisonSearchQuery, setComparisonSearchQuery] = useState<string>('');
  const [debouncingSearch, setDebouncingSearch] = useState<boolean>(false);

  useEffect(() => {
    clearSearchComparison();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SEARCH COMPARISON LOGIC
  const debouncedSearch = React.useRef(
    debounce((query: string) => {
      setDebouncingSearch(false);
      if (currentContact) {
        getContactComparisons(currentContact.contact, query);
      }
    }, 300),
  ).current;

  const clearSearchComparison = () => {
    if (!currentContact) {
      return;
    }

    setComparisonSearchQuery('');
    getContactComparisons(currentContact.contact);
  };

  const onSearchComparison = (query: string) => {
    debouncedSearch.cancel();
    setDebouncingSearch(false);

    if (!query) {
      return clearSearchComparison();
    }

    setComparisonSearchQuery(query);
    setDebouncingSearch(true);
    debouncedSearch(query);
  };

  const onCreateComparison = async () => {
    const { currentContact, user } = props;
    if (!currentContact || !user) {
      return;
    }

    if (userHasPermissionEnabled(user, PERMISSIONS.BMO_GIF)) {
      resetGif();
      skipEmptyState();
      await createComparison(currentContact.contact);
      setBaseDob(currentContact.contact);
      redirect(NAV_PATHS.COMPARISON + COMPARISON_NAV_PATHS.GIF);
      return;
    }
    await createComparison(currentContact.contact);
    redirect(NAV_PATHS.COMPARISON);
  };

  const onComparisonDeleteSuccess = (comparisons: ReducedComparison[]) => {
    removeContactComparisonsFromCurrent(comparisons);
  };

  const renderContent = (): JSX.Element => {
    let contentElement: JSX.Element = <></>;

    if (comparisons?.length || comparisonSearchQuery) {
      contentElement = (
        <ComparisonsTable
          isContactDetailsComparisons
          comparisonSearchQuery={comparisonSearchQuery}
          comparisons={comparisons}
          onComparisonDeleteSuccess={onComparisonDeleteSuccess}
          onOpenComparison={props.onOpenComparison}
          tableColumnsVisibility={{
            savedTo: false,
          }}
        />
      );
    } else {
      const primaryButton = {
        label: `${i18nEmptyStatePath}.PrimaryButtonText`,
        onClick: onCreateComparison,
      };
      contentElement = (
        <EmptyStateCard
          i18nVariables={{ contactName: currentContact?.contact?.fname || '' }}
          messagePath={`${i18nEmptyStatePath}.Message`}
          primaryButton={primaryButton}
          titlePath={`${i18nEmptyStatePath}.Title`}
        />
      );
    }

    return contentElement;
  };

  return (
    <div className="contact-details-comparisons-container table-wrapper table-container">
      <ContactDetailsComparisonsHeader
        comparisons={comparisons}
        comparisonSearchQuery={comparisonSearchQuery}
        isComparisonsLoading={!!isComparisonsLoading}
        debouncingSearch={debouncingSearch}
        user={user}
        onSearchComparison={onSearchComparison}
        clearSearchComparison={clearSearchComparison}
        onCreateComparison={onCreateComparison}
      />
      <div className="contact-details-comparisons-body">{renderContent()}</div>
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  comparisons: state.contacts.current?.comparisons,
  currentContact: state.contacts.current,
  isComparisonsLoading: state.contacts.loading.current.comparisons,
  locale: state.intl.locale,
  user: state.auth.user,
});

export default connect(mapStateToProps, {
  createComparison,
  getContactComparisons,
  removeContactComparisonsFromCurrent,
  resetGif,
  setBaseDob,
  skipEmptyState,
})(withErrorBoundary(ContactDetailsComparisons));
